import { Geometry } from "pixi.js"
import { HillLevels, hillHeightAt } from "game-common/room/hill_height"
import { WaterRegion } from "./water_field"

/** Ground-space UVs on a height-field mesh. One ground row per strip preserves the hill silhouette. */
export const riverBandGeometry = (region: WaterRegion, y0: number, y1: number, hills: HillLevels, left = region.x0, right = region.x0 + region.w): Geometry => {
    left = Math.floor(left / 4) * 4
    right = Math.ceil(right / 4) * 4
    const positions: number[] = [], uvs: number[] = [], indices: number[] = []
    const columns = Math.ceil((right - left) / 4) + 1
    for (let y = y0; y <= y1; y++) for (let c = 0; c < columns; c++) {
        const x = Math.min(right, left + c * 4)
        positions.push(x, y - hillHeightAt(hills, x, y))
        uvs.push((x - region.x0) / region.w, (y - region.y0) / region.h)
        if (y < y1 && c < columns - 1) {
            const i = (y - y0) * columns + c
            indices.push(i, i + 1, i + columns + 1, i, i + columns + 1, i + columns)
        }
    }
    return new Geometry().addAttribute("aVertexPosition", positions, 2).addAttribute("aUvs", uvs, 2).addAttribute("aTextureCoord", uvs, 2).addIndex(indices)
}

interface DepthMesh { visible: boolean; zIndex: number; start: number; size: number }
export interface RiverDepthGroup {
    x0: number; x1: number; y0: number; y1: number; rowIndices: number
    mesh: Pick<DepthMesh, "visible" | "zIndex">
    fine: DepthMesh[]
}

/** Keep the submerged rock foot over the water pieces actually drawing there, including body-sorted pieces. */
export const riverSceneryDepth = (groups: RiverDepthGroup[], x: number, y: number, sink: number, nativeDepth: number): number => {
    let depth = nativeDepth
    for (const group of groups) {
        if (x < group.x0 || x > group.x1 || y < group.y0 || y - sink > group.y1) continue
        if (group.mesh.visible) { depth = Math.max(depth, group.mesh.zIndex + .04); continue }
        for (const mesh of group.fine) {
            if (!mesh.visible) continue
            const top = group.y0 + mesh.start / group.rowIndices
            const bottom = top + mesh.size / group.rowIndices
            if (y >= top && y - sink <= bottom) depth = Math.max(depth, mesh.zIndex + .04)
        }
    }
    return depth
}
